import { useState } from "react";
import { useAtomValue } from "jotai";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { isProcessingOrganizationsAtom } from "@/store/organizationAtoms";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import {
    OrganizationStatus,
    type OrganizationDetail,
    type ResendOrganizationInvitationResponse,
} from "@models/organization";
import { asApiFailure } from "@utils/apiFailure";
import { formatDateTime } from "@utils/formatter";
import { resendInvitationErrorMessage } from "../_helpers/invitation";
import { ResendInvitationDialog } from "./ResendInvitationDialog";

// A 409 (signed up, or cancelled) or 404 (deleted) means the page's picture of
// the client is out of date.
const STALE_CLIENT_STATUSES = [404, 409];

interface ResendInvitationProps {
    organization: OrganizationDetail;
    // Re-reads the client detail, so the page shows what the API now knows.
    onChanged: () => void;
    // A new link went out: any earlier link the page mentions no longer works.
    onSent?: () => void;
}

// Admin "resend invitation" (#438 / OB-02) for a client whose applicant has
// not signed up: their link expired or never arrived. ClientDetail keys it by
// the client's id, so one client's outcome never shows on another's page.
export const ResendInvitation = ({
    organization,
    onChanged,
    onSent,
}: ResendInvitationProps) => {
    const { resendOrganizationInvitation } = useOrganizationApi();
    // The one in-flight source is the page's organization write flag, which
    // resendOrganizationInvitation raises and ClientDetail's status buttons
    // read: one organization write at a time on this page. The guard covers
    // two clicks in one tick, before that flag has rendered.
    const isProcessing = useAtomValue(isProcessingOrganizationsAtom);
    const runGuarded = useSubmitGuard();

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [sent, setSent] =
        useState<ResendOrganizationInvitationResponse | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // The API's own rule (an invitation, none used yet), not the user list. A
    // refusal's reason outlives the control: after a 409 the re-read can show
    // the applicant signed up, and the admin still sees why nothing was sent.
    if (!organization.isAwaitingSignup && !errorMessage) {
        return null;
    }

    const runResend = () =>
        runGuarded(async () => {
            // Drop the previous outcome up front, so a confirmation never sits
            // beside the attempt that just failed.
            setSent(null);
            setErrorMessage(null);

            try {
                setSent(await resendOrganizationInvitation(organization.id));
                onSent?.();
            } catch (error) {
                setErrorMessage(resendInvitationErrorMessage(error));
                const status = asApiFailure(error)?.status;
                if (status !== undefined && STALE_CLIENT_STATUSES.includes(status)) {
                    onChanged();
                }
            } finally {
                setIsConfirmOpen(false);
            }
        });

    // Like approve (DecisionLog 3.4a), no control for a request the API is
    // certain to refuse: a cancelled client is reactivated first.
    const isCancelled = organization.status === OrganizationStatus.Cancelled;

    return (
        <div className="mt-3 space-y-3">
            {organization.isAwaitingSignup && isCancelled && (
                <p className="text-sm text-neutral/70">
                    The applicant hasn&apos;t finished signing up. Reactivate this
                    client to send its applicant a new signup link.
                </p>
            )}

            {organization.isAwaitingSignup && !isCancelled && (
                <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-neutral/70">
                        The applicant hasn&apos;t finished signing up.
                    </span>
                    <Button
                        color={ComponentColor.Primary}
                        size={ComponentSize.SM}
                        disabled={isProcessing}
                        onClick={() => setIsConfirmOpen(true)}
                    >
                        Resend invitation
                    </Button>
                </div>
            )}

            {sent && (
                <p role="status" className="text-sm font-medium">
                    New signup link sent to {sent.email}. It works until{" "}
                    {formatDateTime(sent.expiresAt)}.
                </p>
            )}

            {errorMessage && (
                <p role="alert" className="text-sm text-error">
                    {errorMessage}
                </p>
            )}

            {isConfirmOpen && (
                <ResendInvitationDialog
                    email={organization.email}
                    isProcessing={isProcessing}
                    onConfirm={() => void runResend()}
                    onClose={() => setIsConfirmOpen(false)}
                />
            )}
        </div>
    );
};
